import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CustomAttributeType,
    formatDateTimeString,
    ICustomAttribute,
    IRegistration,
    LabelValue,
    Widget,
} from '../../../shared';
import { ExpandMore, Inventory2TwoTone } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useTheme } from '../../../main';

interface Props {
    lastRegistrations: IRegistration[];
}

export const RegistrationLastRegistrations: FC<Props> = ({ lastRegistrations }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const lastRegistrationsOfEachSupportType = lastRegistrations.reduce(
        (result: IRegistration[][], current: IRegistration) => {
            const supportType =
                result.find((group) => group.length > 0 && group[0].supportTypeId === current.supportTypeId) || [];

            if (supportType.length === 0) result.push(supportType);
            supportType.push(current);
            return result;
        },
        [] as IRegistration[][],
    );

    const formatMessage = (registration: IRegistration) => {
        const mappedAttributes = registration.attributes
            ?.filter((attribute: ICustomAttribute) => attribute.booleanValue || attribute.amountValue)
            .map(({ type, amountValue, name }: ICustomAttribute) =>
                type === CustomAttributeType.AMOUNT ? `${amountValue} x ${name}` : name,
            )
            .join(', ');

        const date = formatDateTimeString(registration.dateOfRegistration);
        const attributes = mappedAttributes ? `- ${mappedAttributes}` : '';
        const organisation = registration.organisation?.name ? `(${registration.organisation.name})` : '';
        const comment = registration.comment ? `(${registration.comment})` : '';

        return `${date} ${organisation} ${attributes} ${comment}`;
    };

    return (
        <Widget icon={<Inventory2TwoTone color="primary" />} title={t('lastRegistrations')} hasTable>
            {!lastRegistrations.length ? (
                <Typography variant="caption" sx={{ opacity: 0.7, p: 2 }}>
                    {t('emptyStateRegistrations')}
                </Typography>
            ) : (
                <Stack spacing={0}>
                    {lastRegistrationsOfEachSupportType?.map((group) => (
                        <Accordion
                            disableGutters
                            sx={{ m: 0, boxShadow: 'none', borderTop: `1px solid ${theme.palette.divider}` }}
                        >
                            <AccordionSummary
                                expandIcon={group?.length > 1 && <ExpandMore />}
                                sx={{
                                    '.MuiAccordionSummary-content': { my: 1, '.Mui-expanded': { mb: 0 } },
                                }}
                            >
                                <LabelValue
                                    data-testid="registrations-type"
                                    label={group[0].supportType?.name ?? ''}
                                    value={
                                        <Typography
                                            variant="caption"
                                            data-testid="registrations-data"
                                            sx={{ opacity: 0.7 }}
                                        >
                                            {formatMessage(group[0])}
                                        </Typography>
                                    }
                                />
                            </AccordionSummary>
                            {group?.length > 1 && (
                                <AccordionDetails sx={{ py: 0 }}>
                                    {group.slice(1)?.map((registration) => (
                                        <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                                            {formatMessage(registration)}
                                        </Typography>
                                    ))}
                                </AccordionDetails>
                            )}
                        </Accordion>
                    ))}
                </Stack>
            )}
        </Widget>
    );
};
